<template>
  <div :class="`propeller-delivery-date ${containerClass}`">
    <div
      class="propeller-delivery-date__grid grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3"
      role="radiogroup"
      :aria-label="getLabel('deliveryDateLabel', 'Delivery date')"
    >
      <template :key="index" v-for="(dateStr, index) in upcomingDates">
        <div
          @click="async (event) => handleSelect(dateStr)"
          @keydown="radioGroupKeydown"
          role="radio"
          :aria-checked="selectedDayKey !== '' && upcomingDayKeys[index] === selectedDayKey ? 'true' : 'false'"
          :tabindex="radioTabIndex(selectedDayKey !== '' && upcomingDayKeys[index] === selectedDayKey, index, selectedDayKey !== '')"
          :data-selected="selectedDayKey !== '' && upcomingDayKeys[index] === selectedDayKey ? 'true' : 'false'"
          :class="`propeller-delivery-date__option cursor-pointer border border-border rounded-[var(--radius-container)] p-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-1 text-center transition-all ${
            selectedDayKey !== '' && upcomingDayKeys[index] === selectedDayKey
              ? 'border-secondary bg-secondary/5 shadow-sm'
              : 'hover:border-secondary/30'
          }`"
        >
          <div class="propeller-delivery-date__option-label font-semibold">
            {{ formatDisplay(dateStr) }}
          </div>
        </div>
      </template>
      <template v-if="showDatePicker">
        <div
          @click="async (event) => openModal()"
          @keydown="radioGroupKeydown"
          role="radio"
          :aria-checked="isCustomDateSelected ? 'true' : 'false'"
          aria-haspopup="dialog"
          :tabindex="radioTabIndex(isCustomDateSelected, upcomingDates.length, selectedDayKey !== '' || isCustomDateSelected)"
          :data-selected="isCustomDateSelected ? 'true' : 'false'"
          data-custom="true"
          :class="`propeller-delivery-date__option propeller-delivery-date__option--custom cursor-pointer border border-border rounded-[var(--radius-container)] p-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-1 text-center transition-all ${
            isCustomDateSelected
              ? 'border-secondary bg-secondary/5 shadow-sm'
              : 'hover:border-secondary/30'
          }`"
        >
          <template v-if="isCustomDateSelected">
            <div class="propeller-delivery-date__option-label font-semibold">
              {{ formatDisplay(selectedDate) }}
            </div>
          </template>

          <template v-if="!isCustomDateSelected">
            <div class="propeller-delivery-date__option-label font-semibold">
              {{ getLabel("pickDate", "Other date...") }}
            </div>
          </template>
        </div>
      </template>
    </div>
    <template v-if="modalOpen">
      <div
        class="propeller-delivery-date__modal fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        @click="async (event) => handleBackdropClick(event)"
      >
        <div
          class="propeller-delivery-date__modal-content bg-card rounded-xl shadow-xl p-6 w-full max-w-sm mx-4"
        >
          <div
            class="propeller-delivery-date__modal-header flex justify-between items-center mb-4"
          >
            <h3
              class="propeller-delivery-date__modal-title text-lg font-semibold"
            >
              {{ getLabel("modalTitle", "Select a delivery date") }}
            </h3>
            <button
              type="button"
              class="propeller-delivery-date__modal-close text-foreground-subtle hover:text-muted-foreground transition-colors"
              @click="async (event) => closeModal()"
            >
              <svg
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                class="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>
          </div>
          <input
            type="date"
            :lang="inputLang"
            class="propeller-delivery-date__input w-full border border-input rounded-[var(--radius-container)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary"
            :class="customDateError ? 'border-destructive focus:ring-destructive focus:border-destructive' : ''"
            :min="minDate"
            :value="customDateValue"
            @change="
              async (event) => handleCustomDateChange((event.target as HTMLInputElement).value)
            "
          />
          <template v-if="customDateError">
            <p
              class="propeller-delivery-date__input-error text-sm text-destructive mt-2"
              role="alert"
            >
              {{ customDateError }}
            </p>
          </template>
          <div
            class="propeller-delivery-date__modal-actions flex justify-end gap-3 mt-4"
          >
            <button
              type="button"
              class="propeller-delivery-date__cancel-btn px-4 py-2 text-sm font-medium text-muted-foreground bg-surface-hover rounded-[var(--radius-container)] hover:bg-accent transition-colors"
              @click="async (event) => closeModal()"
            >
              {{ getLabel("cancel", "Cancel") }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { Cart } from "@propeller-commerce/propeller-sdk-v2";
import { getLabel as _getLabel } from '@propeller-commerce/propeller-v2-core-ui';
import { useInfraProps } from '../composables/vue/useInfraProps';
import { radioGroupKeydown, radioTabIndex } from '../composables/shared/utils/radioGroup';

export interface DeliveryDateProps {
  /** The cart to use for the delivery date */
  cart: Cart;

  /** Show the upcoming N days in the date selector */
  showUpcomingDays?: number;

  /** Skip weekends in the date selector */
  skipWeekends?: boolean;

  /** Show date picker as an option in the date selector */
  showDatePicker?: boolean;

  /** Action when a delivery date is selected */
  onDateSelect?: (date: string) => void;

  /** Custom date display formatting function */
  formatDateDisplay?: (date: string) => string;

  /** Labels for the component */
  labels?: Record<string, string>;

  /** The CSS class for the container */
  containerClass?: string;

  /** Pre-selected date from cart (e.g. cart.postageData.requestDate: "2026-04-17T00:00:00.000Z") */
  initialDate?: string;

  /**
   * Active language/locale (e.g. `'NL'`). Sets the `lang` attribute on the
   * native `<input type="date">` so the browser renders its calendar chrome
   * (month name, weekday headers, Today/Clear) in that locale. Resolved from
   * `<PropellerProvider>` when omitted. Quick-pick tile text is localized
   * separately via `labels` (day_N / month_N keys).
   */
  language?: string;
}
interface DeliveryDateState {
  selectedDate: string;
  modalOpen: boolean;
  customDateValue: string;
  upcomingDays: number;
  skipWeekends: boolean;
  showDatePicker: boolean;
  isCustomDateSelected: boolean;
  containerClass: string;
  upcomingDates: string[];
  minDate: string;
  getLabel: (key: string, fallback: string) => string;
  toApiDate: (date: Date) => string;
  formatDisplay: (isoDate: string) => string;
  handleSelect: (isoDate: string) => void;
  handleCustomDateChange: (value: string) => void;
  openModal: () => void;
  closeModal: () => void;
  handleBackdropClick: (event: Event) => void;
}

const props = withDefaults(defineProps<DeliveryDateProps>(), {
  showUpcomingDays: 3,
  skipWeekends: true,
  showDatePicker: true,
});
// Explicit props win; otherwise infra (e.g. `language`) resolves from
// <PropellerProvider>.
const infra = useInfraProps(props);
const inputLang = computed(() =>
  infra.language ? String(infra.language).toLowerCase() : undefined,
);
const selectedDate = ref<DeliveryDateState["selectedDate"]>("");
const modalOpen = ref<DeliveryDateState["modalOpen"]>(false);
const customDateValue = ref<DeliveryDateState["customDateValue"]>("");
const customDateError = ref<string>("");

const upcomingDays = computed(() => {
  return props.showUpcomingDays !== undefined ? props.showUpcomingDays : 3;
});
const skipWeekends = computed(() => {
  return props.skipWeekends !== undefined ? props.skipWeekends : true;
});
const showDatePicker = computed(() => {
  return props.showDatePicker !== undefined ? props.showDatePicker : true;
});
// Compare by local calendar day, not raw ISO string — see `toDayKey`. The
// selected date counts as "one of the tiles" when its day matches a tile's day,
// regardless of the time/offset the cart stored it with.
const selectedDayKey = computed(() => toDayKey(selectedDate.value));
const upcomingDayKeys = computed(() => upcomingDates.value.map(toDayKey));
const isCustomDateSelected = computed(() => {
  return (
    selectedDayKey.value !== "" &&
    upcomingDayKeys.value.indexOf(selectedDayKey.value) === -1
  );
});
const containerClass = computed(() => {
  return props.containerClass || "delivery-date";
});
const upcomingDates = computed(() => {
  const days: string[] = [];
  const today = new Date();
  const current = new Date(today);
  current.setDate(current.getDate() + 1);
  while (days.length < upcomingDays.value) {
    const dayOfWeek = current.getDay();
    if (!skipWeekends.value || (dayOfWeek !== 0 && dayOfWeek !== 6)) {
      days.push(toApiDate(current));
    }
    current.setDate(current.getDate() + 1);
  }
  return days;
});
const minDate = computed(() => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const y = tomorrow.getFullYear();
  const m = String(tomorrow.getMonth() + 1).padStart(2, "0");
  const d = String(tomorrow.getDate()).padStart(2, "0");
  return y + "-" + m + "-" + d;
});

watch(
  () => [props.initialDate, props.cart],
  () => {
    if (props.initialDate && !selectedDate.value) {
      // Normalize cart format "2026-04-17T00:00:00.000Z" → "2026-04-17T00:00:00Z"
      const dot = props.initialDate.lastIndexOf(".");
      const normalized =
        dot !== -1
          ? props.initialDate.substring(0, dot) + "Z"
          : props.initialDate;
      // The cart's requestDate can be a weekend (e.g. the backend defaults to
      // "tomorrow" without a business-day rule). Adopting it verbatim when
      // skipWeekends is on drops the selected date into the "Other date" tile —
      // it isn't one of the weekday quick-picks — so the soonest/selected date
      // renders LAST, out of sequence, and the "Other date..." entry point is
      // replaced by that date's label. When that happens, snap to the first
      // valid weekday tile (the earliest offered delivery day) instead.
      const parsed = new Date(normalized);
      const isWeekend =
        !isNaN(parsed.getTime()) &&
        (parsed.getDay() === 0 || parsed.getDay() === 6);
      const tiles = upcomingDates.value;
      const adopt =
        skipWeekends.value && isWeekend && tiles.length > 0
          ? tiles[0]
          : normalized;
      selectedDate.value = adopt;
      if (props.onDateSelect) {
        props.onDateSelect(adopt);
      }
    }
  },
  { immediate: true },
);
function getLabel(
  key: string,
  fallback: string,
): ReturnType<DeliveryDateState["getLabel"]> {
  return _getLabel(props.labels, key, fallback);
}
function toApiDate(date: Date): ReturnType<DeliveryDateState["toApiDate"]> {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return y + "-" + m + "-" + d + "T00:00:00Z";
}

/**
 * Local calendar day (`YYYY-MM-DD`) of an ISO date, for comparing a selected /
 * cart date against the quick-pick tiles. Comparing the raw ISO strings is
 * wrong: the cart's `requestDate` carries its own time/offset/millis (e.g.
 * `...T00:00:00.000Z`, `...+02:00`), so a date that IS the same day as a tile
 * fails a string match and gets misclassified as a custom "other" date —
 * duplicating it into the picker tile. Keying by the LOCAL day matches how the
 * tile labels are rendered (`formatDisplay` uses local getDate). Empty/invalid → ''.
 */
function toDayKey(iso: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return y + "-" + m + "-" + d;
}
function formatDisplay(
  isoDate: string,
): ReturnType<DeliveryDateState["formatDisplay"]> {
  if (props.formatDateDisplay) {
    return props.formatDateDisplay(isoDate);
  }
  // Guard against bad input: invalid dates produce NaN/undefined and render
  // as "undefined, undefined NaN". Return an empty string so the caller can
  // decide what to show.
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return "";
  const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  // Weekday / month names go through `labels` so the tile reads in the active
  // locale (e.g. NL "ma, jul 20") instead of hardcoded English. Keys:
  // day_0..day_6 (Sun–Sat), month_0..month_11 (Jan–Dec); English is fallback.
  const weekday = getLabel(`day_${date.getDay()}`, WEEKDAYS[date.getDay()]);
  const month = getLabel(`month_${date.getMonth()}`, MONTHS[date.getMonth()]);
  return weekday + ", " + month + " " + date.getDate();
}
function handleSelect(
  isoDate: string,
): ReturnType<DeliveryDateState["handleSelect"]> {
  selectedDate.value = isoDate;
  modalOpen.value = false;
  if (props.onDateSelect) {
    props.onDateSelect(isoDate);
  }
}
function handleCustomDateChange(
  value: string,
): ReturnType<DeliveryDateState["handleCustomDateChange"]> {
  // Validate before committing. The native date input doesn't reliably enforce
  // the `min` attribute on typed input across browsers, and historical or
  // out-of-range dates parse to a real Date that crashes downstream rendering
  // ("undefined, undefined NaN"). On any failure we keep the typed value in
  // the input so the user can fix it, and surface a single error message.
  customDateValue.value = value;
  if (!value) {
    customDateError.value = "";
    return;
  }
  const parsed = new Date(value + "T00:00:00");
  const year = parsed.getFullYear();
  const isParseable = !isNaN(parsed.getTime()) && year >= 1900 && year <= 9999;
  if (!isParseable) {
    customDateError.value = getLabel(
      "invalidDate",
      "Please enter a valid date.",
    );
    return;
  }
  // Reject anything earlier than minDate (tomorrow). String comparison works
  // because both sides are ISO-formatted YYYY-MM-DD.
  if (value < minDate.value) {
    customDateError.value = getLabel(
      "pastDate",
      "Please select a date in the future.",
    );
    return;
  }
  customDateError.value = "";
  const isoDate = toApiDate(parsed);
  handleSelect(isoDate);
}
function openModal(): ReturnType<DeliveryDateState["openModal"]> {
  customDateError.value = "";
  modalOpen.value = true;
}
function closeModal(): ReturnType<DeliveryDateState["closeModal"]> {
  customDateError.value = "";
  modalOpen.value = false;
}
function handleBackdropClick(
  event: Event,
): ReturnType<DeliveryDateState["handleBackdropClick"]> {
  if (event.target === event.currentTarget) {
    modalOpen.value = false;
  }
}
</script>
